import { useLanguage } from '../contexts/LanguageContext';
import { Check, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const PackagesSection = () => {
  const { t, isRTL, region } = useLanguage();
  const basicFeatures = t('packages.basic.features');
  const basicPriceMap = t('packages.basic.priceYear');
  const individualPriceMap = t('packages.individual.price');
  const basicPrice = (basicPriceMap && basicPriceMap[region]) || '';
  const individualPrice = (individualPriceMap && individualPriceMap[region]) || '';

  const scrollToForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="packages" className="py-12 sm:py-16 md:py-32 bg-slate-50" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-primary">
            {t('packages.title')}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-start max-w-4xl mx-auto">
          {/* Basic Yearly - Featured */}
          <div
            className="bg-white rounded-2xl border-2 border-brand shadow-xl relative overflow-hidden p-5 sm:p-6 md:p-8"
            data-testid="package-basic-yearly"
          >
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
              <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400 fill-amber-400" />
            </div>
            <div className="mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm font-medium text-white bg-brand px-2 sm:px-3 py-1 rounded-full">
                {t('packages.basic.name')}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary" data-testid="package-basic-price">
                {basicPrice}
              </span>
            </div>
            <p className="text-sm sm:text-base text-slate-600 mb-6 sm:mb-8">{t('packages.basic.subtitle')}</p>

            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {Array.isArray(basicFeatures) && basicFeatures.map((feature, index) => (
                <li
                  key={index}
                  className={`flex items-start gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-slate-600">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={scrollToForm}
              className="w-full h-10 sm:h-12 rounded-full bg-brand hover:bg-brand/90 text-white text-sm sm:text-base font-semibold shadow-lg"
              data-testid="package-basic-yearly-btn"
            >
              {t('hero.secondaryBtn')}
            </Button>
          </div>

          {/* Individual */}
          <div
            className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 sm:p-6 md:p-8"
            data-testid="package-individual"
          >
            <div className="mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm font-medium text-amber-600 bg-amber-50 px-2 sm:px-3 py-1 rounded-full">
                {t('packages.individual.name')}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary" data-testid="package-individual-price">
                {individualPrice}
              </span>
            </div>
            <p className="text-sm sm:text-base text-slate-600 mb-6 sm:mb-8">{t('packages.individual.subtitle')}</p>

            <div className="bg-slate-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
              <p className="text-sm sm:text-base text-slate-700 font-medium text-center">
                {t('packages.individual.cta')}
              </p>
            </div>

            <Button
              onClick={scrollToForm}
              variant="outline"
              className="w-full h-10 sm:h-12 rounded-full border-2 border-brand text-brand hover:bg-brand/5 text-sm sm:text-base font-semibold"
              data-testid="package-individual-btn"
            >
              {t('hero.secondaryBtn')}
              <ArrowRight className={`w-3 h-3 sm:w-4 sm:h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
